import { createFileRoute, Link } from "@tanstack/react-router";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD } from "@/lib/structured-data";
import { MarketingShell, MarketingHero } from "@/components/MarketingShell";
import { PageContainer } from "@/components/Logo";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/delete-account")({
  component: DeleteAccountPage,
  head: () => ({
    meta: [
      { title: "Delete your MissionReady account and data" },
      {
        name: "description",
        content:
          "How to request deletion of your MissionReady account and associated data, what is deleted immediately, what is retained, and for how long.",
      },
      ...socialMeta({
        title: "Delete your MissionReady account",
        description: "Steps to delete your MissionReady account and data, what is deleted, and what is retained.",
        url: "https://missionready.dev/delete-account",
      }),
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/delete-account" }],
    scripts: [...ORG_WEBSITE_JSONLD],
  }),
});

const DELETED = [
  "Your sign-in credentials and authentication records",
  "Your profile: name, photo, email, phone number, and job title",
  "Your membership in every MissionReady workspace, ending all access",
  "Your saved app preferences, navigation layout, and notification settings",
  "Your device push-notification registrations",
  "Your stored location history that is not attached to a submitted operational record",
];

const RETAINED = [
  ["Shift, patrol, checkpoint, incident, and D.A.R. records you submitted", "Retained by the security company that employs you, as their operational and legal record of work performed. Retention is set by that company."],
  ["Original D.A.R. audio recordings attached to submitted reports", "Retained with the report by the employing company as evidence of the record."],
  ["Billing and payment history (paid workspaces only)", "Retained up to 7 years where tax and accounting law requires it."],
  ["Security and audit log entries", "Retained up to 12 months for fraud and abuse prevention."],
];

function DeleteAccountPage() {
  return (
    <MarketingShell>
      <MarketingHero
        eyebrow="ACCOUNT DELETION"
        title={<>Delete your MissionReady account</>}
        subtitle="MissionReady (missionready.dev), published by MissionReady, lets you delete your account and associated data at any time. Here is exactly how, and what happens to your data."
      />

      <PageContainer className="pb-20">
        <section className="mx-auto max-w-3xl space-y-10">
          <div>
            <h2 className="font-display text-2xl font-bold">How to request deletion — in the app</h2>
            <ol className="mt-4 list-decimal space-y-2 pl-6 text-sm text-muted-foreground">
              <li>Open the MissionReady app or go to missionready.dev and sign in.</li>
              <li>Open the menu and tap <strong className="text-foreground">Settings</strong>.</li>
              <li>Tap <strong className="text-foreground">Account</strong>.</li>
              <li>Under <strong className="text-foreground">Delete my account</strong>, type <strong className="text-foreground">DELETE</strong> to confirm.</li>
              <li>Tap <strong className="text-foreground">Delete my account permanently</strong>. Your account and profile are removed immediately and you are signed out.</li>
            </ol>
            <p className="mt-3 text-sm text-muted-foreground">
              If you own a workspace that still has other members, hand ownership to another member on the Team page
              first, so your colleagues keep their access.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button asChild>
                <Link to="/settings/account">Go to account deletion</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/contact">Request deletion by email</Link>
              </Button>
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl font-bold">How to request deletion — by email</h2>
            <p className="mt-3 text-sm text-muted-foreground">
              If you cannot sign in, use our <Link to="/contact" className="text-primary underline">contact form</Link> and
              ask for account deletion from the email address on the account. We verify ownership of the address and
              complete deletion within 30 days.
            </p>
          </div>

          <div>
            <h2 className="font-display text-2xl font-bold">Data deleted immediately</h2>
            <ul className="mt-4 list-disc space-y-2 pl-6 text-sm text-muted-foreground">
              {DELETED.map((d) => <li key={d}>{d}</li>)}
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl font-bold">Data retained, and for how long</h2>
            <p className="mt-3 text-sm text-muted-foreground">
              MissionReady is a workplace tool. Records you file during a shift belong to the security company that
              employs you, so deleting your personal account does not delete their operational records.
            </p>
            <dl className="mt-4 space-y-4 text-sm">
              {RETAINED.map(([what, why]) => (
                <div key={what} className="rounded-xl border border-border bg-card p-4">
                  <dt className="font-semibold">{what}</dt>
                  <dd className="mt-1 text-muted-foreground">{why}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 text-sm text-muted-foreground">
              Backups containing deleted personal data expire within 30 days. To ask the security company that employs
              you to delete the operational records that name you, contact that company directly; we support their
              administrators in carrying out those requests.
            </p>
          </div>

          <div>
            <h2 className="font-display text-2xl font-bold">Deleting some data without deleting your account</h2>
            <p className="mt-3 text-sm text-muted-foreground">
              You can withdraw location tracking consent and clear your stored location history at any time under
              Settings → Location privacy, and you can turn off push notifications under Settings → Notifications,
              without deleting your account.
            </p>
          </div>
        </section>
      </PageContainer>
    </MarketingShell>
  );
}
